import type { SearchResourceType, SearchResponse } from "@cohesive/api-client";
import { useQuery } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import { orgAPI } from "./api";

export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2;
const GLOBAL_SEARCH_DEBOUNCE_MS = 250;

/**
 * Debounced global search across workspaces, canvases, chats, files, and
 * folders. The query only fires once the (trimmed) input reaches the backend's
 * 2-character minimum; previous results are kept while typing so the panel
 * doesn't flicker.
 */
export function useGlobalSearch(
  query: string,
  options?: {
    /** Restrict to these resource types (the per-group "show more" view). */
    types?: SearchResourceType[];
    /** Per-type limit (default 5, max 20). */
    limit?: number;
    enabled?: boolean;
  },
) {
  const trimmed = query.trim();
  const [debouncedQuery] = useDebounceValue(trimmed, GLOBAL_SEARCH_DEBOUNCE_MS);

  const enabled =
    (options?.enabled ?? true) &&
    debouncedQuery.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

  const result = useQuery<SearchResponse>({
    queryKey: [
      "global-search",
      debouncedQuery,
      options?.types ?? [],
      options?.limit ?? null,
    ],
    queryFn: () =>
      orgAPI.search.global({
        q: debouncedQuery,
        types: options?.types,
        limit: options?.limit,
      }),
    enabled,
    // Deliberately NO keepPreviousData/placeholderData: bridging results
    // across query keys is what made previous sessions' hits flash under new
    // input (the bridge is observer-level, so no amount of cache clearing
    // fixes it). Without it, data always belongs to the current key — the
    // price is a brief "Searching…" between refinements, which the 250ms
    // debounce already keeps rare.
    staleTime: 15_000,
    // Every debounced input mints its own key; nothing ever reads a restored
    // entry (the search panel always mounts closed with empty input), so
    // persisting them is pure localStorage bloat.
    meta: { persist: false },
  });

  return {
    ...result,
    /**
     * True while the live input hasn't reached the debounced query yet.
     * `data`/`isFetching` describe the debounced query — consumers deciding
     * between "Searching…" and "No results." must treat a debouncing gap as
     * in-flight, or a fresh keystroke reads as an empty result.
     */
    isDebouncing: trimmed !== debouncedQuery,
  };
}
